import {
  ApplicationInsights,
  DistributedTracingModes,
  type IApplicationInsights,
} from "@microsoft/applicationinsights-web";

const connectionString =
  import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING ?? "";
const enabled = import.meta.env.VITE_APPINSIGHTS_ENABLED === "true";

const UID_CLAIM =
  "http://schemas.ofcourse.org/ws/2025/01/identity/claims/uid";

export const LEVEL_TEST_SESSION_HEADER = "X-Level-Test-Session-Id";

let appInsights: IApplicationInsights | null = null;
let levelTestSessionId: string | null = null;

function decodeUserIdFromToken(token: string): string | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join("")
    );
    const decoded = JSON.parse(jsonPayload) as Record<string, string | undefined>;
    return decoded[UID_CLAIM] ?? decoded.sub ?? null;
  } catch {
    return null;
  }
}

export function initAppInsights(): IApplicationInsights | null {
  if (!enabled || !connectionString) {
    return null;
  }

  if (appInsights) {
    return appInsights;
  }

  const instance = new ApplicationInsights({
    config: {
      connectionString,
      enableCorsCorrelation: true,
      distributedTracingMode: DistributedTracingModes.W3C,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      disableFetchTracking: false,
      disableAjaxTracking: false,
      // Third-party payment domains must not receive the W3C traceparent
      // header: it turns simple GETs into preflighted requests, and the
      // Mercado Pago CDN (http2.mlstatic.com) returns a malformed OPTIONS
      // response (duplicated Access-Control-Allow-Origin), so the preflight
      // fails and the card Brick never mounts.
      correlationHeaderExcludedDomains: [
        "*.mlstatic.com",
        "*.mercadopago.com",
        "*.mercadopago.com.br",
        "*.mercadolibre.com",
        "*.mercadolivre.com.br",
        "*.stripe.com",
        "*.paypal.com",
      ],
    },
  });

  instance.loadAppInsights();
  instance.addTelemetryInitializer((envelope) => {
    envelope.tags = envelope.tags ?? {};
    envelope.tags["ai.cloud.role"] = "ofcourse-frontend";

    if (levelTestSessionId) {
      envelope.data = envelope.data ?? {};
      const baseData = envelope.data as { properties?: Record<string, string> };
      baseData.properties = {
        ...(baseData.properties ?? {}),
        levelTestSessionId,
      };
    }
  });

  appInsights = instance;
  return appInsights;
}

export function getAppInsights(): IApplicationInsights | null {
  return appInsights;
}

export function isAppInsightsEnabled(): boolean {
  return enabled && !!connectionString && !!appInsights;
}

export function trackPageView(path: string): void {
  appInsights?.trackPageView({ name: path, uri: path });
}

export function trackEvent(
  name: string,
  properties?: Record<string, string>
): void {
  appInsights?.trackEvent({ name }, properties);
}

export function setLevelTestSessionId(sessionId: string | null): void {
  levelTestSessionId = sessionId;
}

export function getLevelTestSessionId(): string | null {
  return levelTestSessionId;
}

export function syncAuthenticatedUserContext(token: string): void {
  if (!appInsights) return;
  const userId = decodeUserIdFromToken(token);
  if (userId) {
    appInsights.setAuthenticatedUserContext(userId, undefined, true);
  }
}

export function clearAuthenticatedUserContext(): void {
  appInsights?.clearAuthenticatedUserContext();
}

export function restoreAuthenticatedUserContextFromStorage(): void {
  const token = localStorage.getItem("token");
  if (token) {
    syncAuthenticatedUserContext(token);
  }
}
